/* eslint-disable global-require */
import 'core-js/features/global-this';
import { setDefaultOptions } from 'date-fns';
import { cs, hu, sk } from 'date-fns/locale';
import { noop } from 'lodash';
import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import storage from 'store2';

import { LANGUAGE } from '@skippay/core/constants/queryAndStorageKeys';
import { setLocale } from '@skippay/core/global/errorTracking';
import { AppLanguage } from '@skippay/core/i18n/languages';

if (!globalThis.Intl.PluralRules) {
	require('@formatjs/intl-pluralrules/polyfill-force');
	require('@formatjs/intl-pluralrules/locale-data/cs');
	require('@formatjs/intl-pluralrules/locale-data/sk');
	require('@formatjs/intl-pluralrules/locale-data/hu');
}

if (!globalThis.Intl.RelativeTimeFormat) {
	require('@formatjs/intl-relativetimeformat/polyfill-force');
	require('@formatjs/intl-relativetimeformat/locale-data/cs');
	require('@formatjs/intl-relativetimeformat/locale-data/sk');
	require('@formatjs/intl-relativetimeformat/locale-data/hu');
}

type State = {
	locale: AppLanguage;
	messages: Record<string, string>;
};

interface LocaleContextProps {
	locale: AppLanguage;
	switchLocale: (newLocale: State['locale']) => void;
	messages: State['messages'];
	hasMissingLocale: boolean;
}

export const LocaleContext = createContext<LocaleContextProps>({
	locale: AppLanguage.cs,
	switchLocale: noop,
	messages: {},
	hasMissingLocale: false,
});

interface LocaleProviderProps {
	children: ReactNode;
	getAppLanguage: (newLocale?: State['locale']) => State | null;
	appLanguage: State | null;
	onLanguageChange?: (newLocale: State['locale']) => void;
}

function setDefaultFns(locale: AppLanguage): void {
	switch (locale) {
		case AppLanguage.cs:
			setDefaultOptions({ locale: cs });
			break;
		case AppLanguage.sk:
			setDefaultOptions({ locale: sk });
			break;
		case AppLanguage.hu:
			setDefaultOptions({ locale: hu });
			break;
		default:
			setDefaultOptions({ locale: cs });
			break;
	}
}

export function LocaleProvider(props: LocaleProviderProps): JSX.Element {
	const { children, appLanguage, onLanguageChange, getAppLanguage } = props;

	const [state, setState] = useState<State>(
		appLanguage || { locale: AppLanguage.cs, messages: {} }
	);

	const [hasMissingLocale, setHasMissingLocale] = useState<boolean>(appLanguage === null);

	useEffect(() => {
		setLocale(state.locale);
		setDefaultFns(state.locale);
	}, [state.locale]);

	async function switchLocale(newLocale: State['locale']) {
		const result = getAppLanguage(newLocale);

		if (!result) {
			return;
		}

		storage.set(LANGUAGE, newLocale);

		setState(result);
		onLanguageChange?.(newLocale);

		setHasMissingLocale(false);
	}

	return (
		<LocaleContext.Provider value={{ switchLocale, ...state, hasMissingLocale }}>
			{children}
		</LocaleContext.Provider>
	);
}

export function useLocaleContext(): LocaleContextProps {
	return useContext(LocaleContext);
}
